import { Component, EventEmitter, HostListener, OnInit, OnDestroy, Output } from '@angular/core';
import { ScreenSize, screenSizeEqOrLessThan } from '@nano/common/util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';

import { NavService } from '../nav.service';
import { PaymentMenuItemsService } from './payment-menu-items.service';

@UntilDestroy()
@Component({
  selector: 'app-payment-menu-items',
  templateUrl: './payment-menu-items.component.html',
  styleUrls: ['./payment-menu-items.component.less'],
  standalone: false
})
export class PaymentMenuItemsComponent implements OnInit, OnDestroy {
  @Output() itemClicked: EventEmitter<void>;

  readonly makePaymentEnabled$: Observable<boolean>;
  readonly importPaymentsEnabled$: Observable<boolean>;
  readonly importPaymentsVisible$: Observable<boolean>;
  readonly scheduledPaymentsEnabled$: Observable<boolean>;
  readonly scheduledPaymentsVisible$: Observable<boolean>;
  readonly paymentBeneficiariesVisible$: Observable<boolean>;
  readonly paymentBeneficiariesEnabled$: Observable<boolean>;
  readonly pendingPaymentsVisible$: Observable<boolean>;
  readonly pendingPaymentsEnabled$: Observable<boolean>;
  readonly pendingOperationsCount$: Observable<number>;

  isScreenSizeEqOrLessThanSM = true;
  private breakpointSubscription: Subscription;

  constructor(
    private navService: NavService,
    private paymentMenuItemsService: PaymentMenuItemsService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.itemClicked = this.paymentMenuItemsService.actionPerformed$;

    this.makePaymentEnabled$ = this.paymentMenuItemsService.makePaymentEnabled$;
    this.importPaymentsEnabled$ = this.paymentMenuItemsService.importPaymentsEnabled$;
    this.importPaymentsVisible$ = this.paymentMenuItemsService.importPaymentsVisible$;
    this.scheduledPaymentsEnabled$ = this.paymentMenuItemsService.scheduledPaymentsEnabled$;
    this.scheduledPaymentsVisible$ = this.paymentMenuItemsService.scheduledPaymentsVisible$;
    this.paymentBeneficiariesVisible$ = this.paymentMenuItemsService.paymentBeneficiariesVisible$;
    this.paymentBeneficiariesEnabled$ = this.navService.userHasPartyAndRealAccountAndNotFailed$;
    this.pendingPaymentsVisible$ = this.navService.pendingPaymentsVisible$;
    this.pendingPaymentsEnabled$ = this.navService.userHasPartyAndRealAccountAndNotFailed$;
    this.pendingOperationsCount$ = this.navService.pendingOperationsCount$;
  }

  ngOnInit(): void {
    this.breakpointSubscription = this.breakpointObserver
      .observe([ScreenSize.SM])
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.checkScreenSizeEqOrLessThanSM();
      });

    this.checkScreenSizeEqOrLessThanSM();
  }

  ngOnDestroy(): void {
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
  }

  @HostListener('window:resize') onWindowResize(): void {
    this.checkScreenSizeEqOrLessThanSM();
  }

  pendingPayments(): void {
    this.paymentMenuItemsService.pendingPayments().pipe(take(1)).subscribe();
  }

  makePayment(): void {
    this.paymentMenuItemsService.makePayment().pipe(take(1)).subscribe();
  }

  importPayments(): void {
    this.paymentMenuItemsService.importPayments().pipe(take(1)).subscribe();
  }

  scheduledPayments(): void {
    this.paymentMenuItemsService.scheduledPayments().pipe(take(1)).subscribe();
  }

  paymentBeneficiaries(): void {
    this.paymentMenuItemsService.paymentBeneficiaries().pipe(take(1)).subscribe();
  }

  private checkScreenSizeEqOrLessThanSM(): void {
    this.isScreenSizeEqOrLessThanSM = screenSizeEqOrLessThan(ScreenSize.SM, this.breakpointObserver);
  }
}
